import React, { Fragment, useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Container, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { FaMapMarkerAlt, FaLocationArrow } from "react-icons/fa";
import { Upload, ArrowLeft } from "react-feather";
import Loader from "../../shared/Loader";
import { errorToast, successToast } from "../../shared/Toast";
import Breadcrumb from "../common/breadcrumb";
import { addBranch } from "../../utils/apiCalls";
import { useSelector } from "react-redux";
import { C, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard } from "../reporting/ReportingShared";
import ToggleSwitch from "../../shared/ToggleSwitch";

const AddBranch = () => {
  const navigate = useNavigate();
  const userType = localStorage.getItem("userType");
  const merchantIdFromStorage = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchant = userType === "merchant" ? merchantIdFromStorage : reduxSelectedMerchant;

  const [loading, setLoading] = useState(false);
  const [branchData, setBranchData] = useState({
    name: "",
    arabicName: "",
    address: "",
    lat: "",
    lng: "",
    openingTime: "",
    closingTime: "",
    orderCutoffMinutes: 2,
    merchantID: "",
    externalID: "",
    brandReference: "",
    kdsApp: true,
    kdsWeb: false,
    menuPaymentMode: "cash",
  });
  const [modal, setModal] = useState(false);
  const mapRef = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [imageForPayload, setImageForPayload] = useState(null);
  const [dummyImg, setDummyImg] = useState("https://via.placeholder.com/80");

  const toggleModal = () => {
    if (modal) {
      if (map.current && marker.current) marker.current.setMap(null);
      map.current = null;
    }
    setModal(!modal);
  };

  const handleInputChange = (e, key) => {
    setBranchData((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const validateTimes = () => {
    if (!branchData.openingTime || !branchData.closingTime) {
      return { isValid: false, message: "Both opening and closing times are required" };
    }
    // Closing earlier than opening is valid: it means the branch closes the next day
    // (overnight hours, e.g. open 18:00, close 03:00).
    return { isValid: true };
  };

  const _handleImgChange = (e) => {
    e.preventDefault();
    const image = e.target.files[0];
    if (!image) return;
    setImageForPayload(image);
    const reader = new FileReader();
    reader.onload = () => setDummyImg(reader.result);
    reader.readAsDataURL(image);
  };

  const handleValidSubmit = async (e) => {
    e.preventDefault();

    if (!imageForPayload) {
      errorToast("Image is required");
      return;
    }

    const timeValidation = validateTimes();
    if (!timeValidation?.isValid) {
      errorToast(timeValidation.message);
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("merchantID", branchData.merchantID);
      formData.append("name", branchData.name);
      formData.append("arabicName", branchData.arabicName);
      formData.append("address", branchData.address);
      formData.append("lat", `${branchData.lat}`);
      formData.append("lng", `${branchData.lng}`);
      formData.append("openingTime", branchData.openingTime);
      formData.append("closingTime", branchData.closingTime);
      formData.append("orderCutoffMinutes", branchData.orderCutoffMinutes);
      if (branchData.externalID) formData.append("externalID", branchData.externalID);
      if (branchData.brandReference) formData.append("brandReference", branchData.brandReference);
      formData.append("kdsApp", branchData.kdsApp);
      formData.append("kdsWeb", branchData.kdsWeb);
      formData.append("menuPaymentMode", branchData.menuPaymentMode || "cash");
      formData.append("dataType", "branch");
      if (imageForPayload) formData.append("image1", imageForPayload);

      const response = await addBranch(formData);

      if (response) {
        successToast("Branch added successfully");
        navigate("/branches/branchesList");
        return;
      }
      errorToast(response?.data?.message ?? "Error adding branch");
    } catch (error) {
      console.error("Error adding branch:", error);
      errorToast("Failed to add branch");
    } finally {
      setLoading(false);
    }
  };

  const handleDiscard = () => navigate("/branches/branchesList");

  const handleDone = () => {
    if (selectedLocation) {
      setBranchData((prev) => ({
        ...prev,
        address: selectedLocation.address,
        lat: `${selectedLocation.lat}`,
        lng: `${selectedLocation.lng}`,
      }));
    }
    toggleModal();
  };

  const handleUseCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const location = new window.google.maps.LatLng(latitude, longitude);

          if (map.current) {
            map.current.setCenter(location);
            map.current.setZoom(15);

            if (marker.current) marker.current.setMap(null);

            marker.current = new window.google.maps.Marker({
              map: map.current,
              position: location,
            });

            setSelectedLocation({
              address: `Lat: ${latitude}, Lng: ${longitude}`,
              lat: latitude,
              lng: longitude,
            });
          }
        },
        (error) => {
          console.error("Error getting location:", error);
          errorToast("Failed to get current location");
        }
      );
    } else {
      errorToast("Geolocation is not supported by this browser");
    }
  };

  useEffect(() => {
    if (selectedMerchant && selectedMerchant !== "all") {
      setBranchData((prev) => ({ ...prev, merchantID: selectedMerchant }));
    } else {
      setBranchData((prev) => ({ ...prev, merchantID: "" }));
    }
  }, [selectedMerchant]);

  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.innerHTML = `
      .pac-container{
        width: 319px;
        position: absolute;
        left: 27px;
        top: 380px !important;
      }
    `;
    document.head.appendChild(styleElement);
    return () => document.head.removeChild(styleElement);
  }, []);

  useEffect(() => {
    if (modal) {
      const initializeMap = () => {
        const mapOptions = {
          center: { lat: 23.8859, lng: 45.0792 },
          zoom: 5,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          styles: [
            {
              featureType: "poi",
              elementType: "labels",
              stylers: [{ visibility: "off" }],
            },
          ],
        };

        map.current = new window.google.maps.Map(mapRef.current, mapOptions);

        if (branchData.lat && branchData.lng) {
          const location = new window.google.maps.LatLng(branchData.lat, branchData.lng);
          marker.current = new window.google.maps.Marker({
            map: map.current,
            position: location,
          });
          map.current.setCenter(location);
          map.current.setZoom(15);
        }

        map.current.addListener("click", (e) => {
          const location = e.latLng;
          if (marker.current) marker.current.setMap(null);
          marker.current = new window.google.maps.Marker({
            map: map.current,
            position: location,
          });
          setSelectedLocation({
            address: `Lat: ${location.lat()}, Lng: ${location.lng()}`,
            lat: location.lat(),
            lng: location.lng(),
          });
        });
      };

      const timer = setTimeout(initializeMap, 500);
      return () => clearTimeout(timer);
    }
  }, [modal, branchData.lat, branchData.lng]);

  useEffect(() => {
    const el = document.getElementById("address-input");
    if (!el || !window.google?.maps?.places) return;
    const autocomplete = new window.google.maps.places.Autocomplete(el, { types: ["geocode"] });

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (!place.geometry) return;
      setBranchData((prev) => ({
        ...prev,
        address: place.formatted_address,
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      }));
    });
  }, []);

  // ---- Styles ----
  const labelStyle = {
    fontSize: 11,
    fontWeight: 700,
    color: C.textLight,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 6,
    display: "block",
  };
  const inputStyle = {
    padding: "10px 12px",
    fontSize: 13,
    borderRadius: 8,
    border: `1.5px solid ${C.border}`,
    fontFamily: "inherit",
    outline: "none",
    color: C.text,
    background: C.white,
    width: "100%",
    transition: "border 0.15s",
  };
  const primaryBtnStyle = {
    background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
    border: "none",
    borderRadius: 8,
    padding: "10px 24px",
    fontWeight: 600,
    color: C.white,
    boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)",
    cursor: "pointer",
    fontSize: 13,
  };
  const ghostBtnStyle = {
    background: C.white,
    border: `1.5px solid ${C.border}`,
    borderRadius: 8,
    padding: "10px 24px",
    fontWeight: 600,
    color: C.text,
    cursor: "pointer",
    fontSize: 13,
  };

  return (
    <Fragment>
      <Breadcrumb title="Add Branch" parent="Branch" />
      <Container fluid>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">
          {loading ? (
            <Loader />
          ) : (
            <RCard>
              {/* Heading */}
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
                <div
                  onClick={handleDiscard}
                  title="Back"
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    border: `1.5px solid ${C.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    background: C.white,
                  }}
                >
                  <ArrowLeft size={16} color={C.textMed} />
                </div>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: C.text, letterSpacing: -0.3 }}>
                    Add Branch
                  </div>
                  <div style={{ fontSize: 12, color: C.textMed, marginTop: 2 }}>
                    Create a new branch location
                  </div>
                </div>
              </div>

              <form onSubmit={handleValidSubmit}>
                {/* Image upload */}
                <div style={{ marginBottom: 24 }}>
                  <label style={labelStyle}>Branch Image *</label>
                  <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                    <img
                      src={dummyImg}
                      alt="preview"
                      style={{
                        width: 80,
                        height: 80,
                        borderRadius: 10,
                        objectFit: "cover",
                        border: `1px solid ${C.border}`,
                      }}
                    />
                    <label
                      style={{
                        ...ghostBtnStyle,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: 12,
                      }}
                    >
                      <Upload size={14} />
                      Choose Image
                      <input type="file" onChange={_handleImgChange} style={{ display: "none" }} accept="image/*" />
                    </label>
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                    gap: 16,
                    marginBottom: 20,
                  }}
                >
                  {branchData.merchantID && (
                    <div>
                      <label style={labelStyle}>External ID</label>
                      <input
                        type="text"
                        value={branchData.externalID}
                        onChange={(e) => handleInputChange(e, "externalID")}
                        required
                        style={inputStyle}
                      />
                    </div>
                  )}

                  <div>
                    <label style={labelStyle}>Brand Reference</label>
                    <input
                      type="text"
                      value={branchData.brandReference}
                      onChange={(e) => handleInputChange(e, "brandReference")}
                      placeholder="Optional"
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>Name *</label>
                    <input
                      type="text"
                      value={branchData.name}
                      onChange={(e) => handleInputChange(e, "name")}
                      required
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>Arabic Name *</label>
                    <input
                      type="text"
                      value={branchData.arabicName}
                      onChange={(e) => handleInputChange(e, "arabicName")}
                      required
                      style={inputStyle}
                    />
                  </div>

                  <div style={{ gridColumn: "1 / -1", position: "relative" }}>
                    <label style={labelStyle}>Address *</label>
                    <input
                      id="address-input"
                      type="text"
                      value={branchData.address}
                      onChange={(e) => handleInputChange(e, "address")}
                      required
                      style={{ ...inputStyle, paddingRight: 40 }}
                    />
                    <FaMapMarkerAlt
                      onClick={toggleModal}
                      size={18}
                      style={{
                        position: "absolute",
                        right: 12,
                        bottom: 11,
                        cursor: "pointer",
                        color: C.coral,
                      }}
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>Latitude *</label>
                    <input
                      type="text"
                      value={`${branchData.lat}`}
                      onChange={(e) => handleInputChange(e, "lat")}
                      required
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>Longitude *</label>
                    <input
                      type="text"
                      value={`${branchData.lng}`}
                      onChange={(e) => handleInputChange(e, "lng")}
                      required
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>Opening Time *</label>
                    <input
                      type="time"
                      value={branchData.openingTime}
                      onChange={(e) => handleInputChange(e, "openingTime")}
                      required
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>Closing Time *</label>
                    <input
                      type="time"
                      disabled={!branchData.openingTime}
                      value={branchData.closingTime}
                      onChange={(e) => handleInputChange(e, "closingTime")}
                      required
                      style={{
                        ...inputStyle,
                        opacity: branchData.openingTime ? 1 : 0.55,
                      }}
                    />
                    {branchData.openingTime &&
                      branchData.closingTime &&
                      branchData.closingTime <= branchData.openingTime && (
                        <div style={{ color: C.blue, fontSize: 11, marginTop: 6, fontWeight: 600 }}>
                          Closes next day (overnight hours)
                        </div>
                      )}
                  </div>

                  <div>
                    <label style={labelStyle}>Order Cut-off (minutes)</label>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={branchData.orderCutoffMinutes}
                      onChange={(e) => handleInputChange(e, "orderCutoffMinutes")}
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>KDS App (offline LAN screens)</label>
                    <ToggleSwitch
                      checked={!!branchData.kdsApp}
                      handleToggle={() =>
                        setBranchData((prev) => ({ ...prev, kdsApp: !prev.kdsApp }))
                      }
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>KDS Web (online order-prep screens)</label>
                    <ToggleSwitch
                      checked={!!branchData.kdsWeb}
                      handleToggle={() =>
                        setBranchData((prev) => ({ ...prev, kdsWeb: !prev.kdsWeb }))
                      }
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>Menu Payment (QR-menu customers)</label>
                    <select
                      style={inputStyle}
                      value={branchData.menuPaymentMode}
                      onChange={(e) =>
                        setBranchData((prev) => ({ ...prev, menuPaymentMode: e.target.value }))
                      }
                    >
                      <option value="cash">Pay at counter</option>
                      <option value="online">Online only (Telr)</option>
                      <option value="both">Customer chooses</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 24, borderTop: `1px solid ${C.borderLight}`, paddingTop: 20 }}>
                  <button type="button" onClick={handleDiscard} style={ghostBtnStyle}>
                    Cancel
                  </button>
                  <button type="submit" style={primaryBtnStyle}>
                    Submit
                  </button>
                </div>
              </form>
            </RCard>
          )}
        </div>
      </Container>

      {/* Map Modal */}
      <Modal isOpen={modal} toggle={toggleModal} size="lg" centered>
        <ModalHeader toggle={toggleModal} style={{ borderBottom: `2px solid ${C.borderLight}` }}>
          <span style={{ fontWeight: 700, color: C.text }}>Select Location</span>
        </ModalHeader>
        <ModalBody style={{ padding: 16 }}>
          <div ref={mapRef} style={{ width: "100%", height: "400px", borderRadius: 10, overflow: "hidden" }} />
          <Button
            onClick={handleUseCurrentLocation}
            style={{
              position: "absolute",
              top: 80,
              right: 32,
              borderRadius: "50%",
              width: 44,
              height: 44,
              padding: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
              border: "none",
              boxShadow: "0 4px 12px rgba(0, 150, 137,0.3)",
            }}
          >
            <FaLocationArrow />
          </Button>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleModal}>
            Cancel
          </Button>
          <button onClick={handleDone} style={primaryBtnStyle}>
            Done
          </button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default AddBranch;
